import { formatCurrency } from "@/lib/money";
import { hasNotInvoicedStayAmount, type StayPaymentGap } from "@/lib/stayPaymentGap";

function notInvoicedExplanation(gap: StayPaymentGap): string {
  const currency = gap.currency;
  const total = formatCurrency(gap.stayTotal, currency);
  const missing = formatCurrency(gap.notInvoicedAmount, currency);
  const paidCoversInvoices = gap.paidAmount + 0.009 >= gap.invoicedAmount;
  if (gap.invoicedAmount > 0 && paidCoversInvoices && gap.paidAmount > 0) {
    return `Bezahlt sind ${formatCurrency(gap.paidAmount, currency)} von ${total}. Die restlichen ${missing} sind noch nicht berechnet und stehen auf keiner Rechnung. Deshalb gibt es keinen offenen Rechnungsbetrag.`;
  }
  if (gap.invoicedAmount > 0) {
    return `${formatCurrency(gap.invoicedAmount, currency)} stehen bereits auf einer Rechnung. Weitere ${missing} sind noch nicht berechnet und gelten deshalb nicht als offener Rechnungsbetrag.`;
  }
  if (gap.paidAmount > 0) {
    const stillDue = formatCurrency(Math.max(0, gap.stayTotal - gap.paidAmount), currency);
    return `Bezahlt sind ${formatCurrency(gap.paidAmount, currency)} von ${total}. Für die übrigen ${stillDue} liegt noch keine Rechnung vor.`;
  }
  return `Für den Aufenthalt über ${total} liegt noch keine Rechnung vor. Der Betrag ist deshalb nicht als offen markiert.`;
}

export function StayPaymentBreakdown({ gap }: { gap: StayPaymentGap }) {
  const missing = hasNotInvoicedStayAmount(gap);
  const currency = gap.currency;

  return (
    <div
      className={
        missing
          ? "rounded-xl border border-status-orange/40 bg-status-orange/10 px-3 py-3"
          : "rounded-xl border border-separator bg-fill-tertiary/30 px-3 py-3"
      }
    >
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
            Aufenthalt
          </dt>
          <dd className="mt-0.5 text-sm font-medium tabular-nums text-label">
            {formatCurrency(gap.stayTotal, currency)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
            Berechnet
          </dt>
          <dd className="mt-0.5 text-sm tabular-nums text-label">
            {formatCurrency(gap.invoicedAmount, currency)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
            Bezahlt
          </dt>
          <dd className="mt-0.5 text-sm tabular-nums text-label">
            {formatCurrency(gap.paidAmount, currency)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
            Noch nicht berechnet
          </dt>
          <dd
            className={
              missing
                ? "mt-0.5 text-sm font-semibold tabular-nums text-status-orange"
                : "mt-0.5 text-sm tabular-nums text-label"
            }
          >
            {formatCurrency(gap.notInvoicedAmount, currency)}
          </dd>
        </div>
      </dl>
      {missing ? (
        <p className="mt-2 text-sm text-label">{notInvoicedExplanation(gap)}</p>
      ) : null}
    </div>
  );
}
